import {
  getConnectorStat,
  getOrderOverview,
  getOrderStat,
  getOrderTrend,
  GetStationByMap,
  GetStationTop10,
  GetUserStat,
} from '@/api/operation'
import { EvseOrderStatistics, GetOrderOverviewReply } from '@/types/cms/user'
import { ConnectorState, StationMapInfo, StationTopInfo, StatisticsTimeGrain } from '@/types/types'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import Moment from 'moment'
export const useChartData = () => {
  let setTimeId: ReturnType<typeof setInterval> | null = null
  const orderOverview = ref<GetOrderOverviewReply>()
  const totalListData = ref<{ label: string; value: string; unit: string }[]>()
  const hour24Trend = ref<{ xAxis: number[]; data: number[] }>({ xAxis: [], data: [] })
  const hour24Total = ref<Partial<EvseOrderStatistics>>()
  const totalEvseStatus = ref<{ name: string; value: string }[]>()
  const stationList = ref<StationTopInfo[]>()
  const stationMap = ref<StationMapInfo[]>()
  const co2EmissionReductionValue = ref<number>(0)
  const plantingTreeValue = ref<number>(0)
  const beforeYesterdayTotal = ref<Partial<EvseOrderStatistics>>()
  const yesterdayTotal = ref<Partial<EvseOrderStatistics>>()
  const userStat = ref<{
    totalNum: number
    actNumByDay: number
    actNumByMon: number
  }>({
    totalNum: 0,
    actNumByDay: 0,
    actNumByMon: 0,
  })
  const month12TredTotal = ref<{
    legendData: string[]
    xAxisData: string[]
    seriesData: number[][]
  }>({
    legendData: [],
    xAxisData: [],
    seriesData: [],
  })
  // 初始化用户统计数据
  const initUserStat = async () => {
    const { totalNum, actNumByDay, actNumByMon } = await GetUserStat()
    userStat.value = {
      totalNum,
      actNumByDay,
      actNumByMon,
    }
  }
  // 初始化站点列表
  const initStationList = () => {
    GetStationTop10().then((res: { list: StationTopInfo[] }) => {
      const { list } = res
      stationList.value = list
    })
    GetStationByMap().then((res: { list: StationMapInfo[] }) => {
      const { list } = res
      stationMap.value = list
    })
  }
  // 获取统计数据-涵盖小时、天、周统计列表
  const initOrderOverview = async () => {
    const data = await getOrderOverview()
    orderOverview.value = data as GetOrderOverviewReply
    _hour24Trend()
    _hour24Total()
  }

  const _yesterdayTrend = async () => {
    // 获取昨天的开始时间和结束时间
    const yesterdayStart = Moment().subtract(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
    const yesterdayEnd = Moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD HH:mm:ss')

    // 获取前天的开始时间和结束时间
    const dayBeforeYesterdayStart = Moment()
      .subtract(2, 'day')
      .startOf('day')
      .format('YYYY-MM-DD HH:mm:ss')
    const dayBeforeYesterdayEnd = Moment()
      .subtract(2, 'day')
      .endOf('day')
      .format('YYYY-MM-DD HH:mm:ss')
    const { stat: orderStatDataYesterday } = await getOrderStat({
      operatorID: '0',
      stationID: '0',
      beginTime: yesterdayStart,
      endTime: yesterdayEnd,
    })
    const { stat: orderStatDataBeforeYesterday } = await getOrderStat({
      operatorID: '0',
      stationID: '0',
      beginTime: dayBeforeYesterdayStart,
      endTime: dayBeforeYesterdayEnd,
    })
    beforeYesterdayTotal.value = orderStatDataBeforeYesterday
    yesterdayTotal.value = orderStatDataYesterday
  }
  const _hour24Trend = async () => {
    const {
      stat: orderStatData,
      co2EmissionReduction,
      plantingTree,
    } = await getOrderStat({
      operatorID: '0',
      stationID: '0',
    })
    co2EmissionReductionValue.value = co2EmissionReduction
    plantingTreeValue.value = plantingTree
    const data = orderOverview.value as GetOrderOverviewReply
    totalListData.value = [
      {
        label: '累计充电次数(次)',
        value: orderStatData.totalOrderNum,
        unit: '',
      },
      {
        label: '累计充电时长(小时)',
        value: Math.floor(orderStatData.totalChargingTime / 3600),
        unit: '',
      },
      {
        label: '累计充电度数(度)',
        value: orderStatData.totalElec,
        unit: '',
      },
      {
        label: '累计充电金额(元)',
        value: orderStatData.totalAmount,
        unit: '',
      },
    ]
    hour24Trend.value = {
      xAxis: new Array(24).fill(0).map((_, index) => index),
      data: data.hourTrend.map((item: EvseOrderStatistics) => item.totalOrderNum),
    }
  }
  const _hour24Total = () => {
    const data = orderOverview.value as GetOrderOverviewReply
    const hour24TotalOrderNum = data.hourTrend.reduce(
      (total: number, item: EvseOrderStatistics) => total + item.totalOrderNum,
      0,
    )
    const hour24TotalElec = data.hourTrend.reduce(
      (total: number, item: EvseOrderStatistics) => total + item.totalElec,
      0,
    )
    const hour24TotalElecAmount = data.hourTrend.reduce(
      (total: number, item: EvseOrderStatistics) => total + item.totalElecAmount,
      0,
    )
    const hour24TotalServiceAmount = data.hourTrend.reduce(
      (total: number, item: EvseOrderStatistics) => total + item.totalServiceAmount,
      0,
    )
    hour24Total.value = {
      totalOrderNum: Math.floor(hour24TotalOrderNum),
      totalElec: Math.floor(hour24TotalElec),
      totalElecAmount: Math.floor(hour24TotalElecAmount),
      totalServiceAmount: Math.floor(hour24TotalServiceAmount),
    }
  }

  const init12MonthTotal = async () => {
    const beginTime = Moment().subtract(11, 'month').format('YYYY-MM-DD HH:mm:ss')
    const endTime = Moment().format('YYYY-MM-DD HH:mm:ss')
    const { stats: orderStatData } = await getOrderTrend({
      operatorID: '0',
      stationID: '0',
      grain: StatisticsTimeGrain.STD_MONTH,
      beginTime,
      endTime,
    })
    const legendData = ['电费', '服务费', '订单数', '电量']
    const xAxisData: string[] = []
    const seriesData: number[][] = [[], [], [], []]
    orderStatData.forEach((item: EvseOrderStatistics) => {
      xAxisData.push(item.tag.split('-')[1])
      seriesData[0].push(item.totalElecAmount)
      seriesData[1].push(item.totalServiceAmount)
      seriesData[2].push(item.totalChargingOrder)
      seriesData[3].push(item.totalElec)
    })
    month12TredTotal.value = {
      legendData,
      xAxisData,
      seriesData,
    }
  }
  const loadConnectorStat = async () => {
    const connectorStatList = await getConnectorStat({
      operatorID: '0',
    })
    const result = new Array(5).fill(0)
    connectorStatList.stats.forEach((stat: any) => {
      switch (stat.state) {
        // '充电中'
        case ConnectorState.CS_Charging:
        case ConnectorState.CS_SuspendedEVSE:
        case ConnectorState.CS_SuspendedEV:
          result[0] += stat.num
          break
        // '空闲中'
        case ConnectorState.CS_Available:
        case ConnectorState.CS_Preparing:
          result[1] += stat.num
          break
        // '离线中'
        case ConnectorState.CS_Unavailable:
          result[2] += stat.num
          break
        // '故障中'
        case ConnectorState.CS_Faulted:
          result[3] += stat.num
          break
        // '占用中'
        case ConnectorState.CS_Reserved:
        case ConnectorState.CS_Finishing:
        case ConnectorState.CS_Waiting:
        case ConnectorState.CS_Occupied:
          result[4] += stat.num
          break
        default:
          break
      }
    })
    totalEvseStatus.value = [
      {
        name: '充电中',
        value: result[0].toString(),
      },
      {
        name: '空闲中',
        value: result[1].toString(),
      },
      {
        name: '离线中',
        value: result[2].toString(),
      },
      {
        name: '故障中',
        value: result[3].toString(),
      },
    ]
  }

  const parcent = computed(() => {
    if (yesterdayTotal.value && beforeYesterdayTotal.value) {
      const { totalAmount, totalChargingTime, totalElec, totalOrderNum } = yesterdayTotal.value
      const {
        totalAmount: totalAmountB,
        totalChargingTime: totalChargingTimeB,
        totalElec: totalElecB,
        totalOrderNum: totalOrderNumB,
      } = beforeYesterdayTotal.value
      const _computedParcent = (before: number, today: number) => {
        // console.log(before, today)
        if (today == 0 && before == 0) {
          return 0
        }
        if (today == 0) {
          return 100
        }

        return Number((((today - before) / before) * 100).toFixed(2))
      }
      return {
        totalAmount: _computedParcent(totalAmount ?? 0, totalAmountB ?? 0),
        totalChargingTime: _computedParcent(totalChargingTime ?? 0, totalChargingTimeB ?? 0),
        totalElec: _computedParcent(totalElec ?? 0, totalElecB ?? 0),
        totalOrderNum: _computedParcent(totalOrderNum ?? 0, totalOrderNumB ?? 0),
      }
    }
    return {
      totalAmount: 0,
      totalChargingTime: 0,
      totalElec: 0,
      totalOrderNum: 0,
    }
  })
  const _fetchChartData = () => {
    initOrderOverview()
    loadConnectorStat()
    initStationList()
    _yesterdayTrend()
    init12MonthTotal()
    initUserStat()
  }
  onMounted(() => {
    _fetchChartData()
    setTimeId = setInterval(() => {
      _fetchChartData()
    }, 5 * 60 * 1000)
  })
  onUnmounted(() => {
    if (setTimeId) {
      clearInterval(setTimeId)
    }
  })
  return {
    orderOverview,
    totalListData,
    hour24Trend,
    hour24Total,
    totalEvseStatus,
    stationList,
    stationMap,
    beforeYesterdayTotal,
    yesterdayTotal,
    parcent,
    co2EmissionReductionValue,
    plantingTreeValue,
    userStat,
    month12TredTotal,
  }
}
